<script lang="ts">
	import { getContextClient } from '@urql/svelte'
	import {
		FetchSignedUploadUrlDocument,
		type FetchSignedUploadUrlQuery,
		type FetchSignedUploadUrlQueryVariables,
	} from '$src/lib/queries/generated/QuerySignedImageUrl'
	import Spinner from '../loading/Spinner.svelte'
	import {
		UpdateProfileDocument,
		type UpdateProfileMutation,
		type UpdateProfileMutationVariables,
	} from '$lib/queries/generated/UpdateProfile'
	import Button from '../buttons/Button.svelte'
	import VerifiedIcon from '../icons/VerifiedIcon.svelte'
	import CameraIcon from '../icons/CameraIcon.svelte'
	import { stopPropagation } from '$lib/utils/clickHelpers'
	import { createImageErrorHandler } from '$lib/utils/image-error-handler'

	interface Props {
		currentProfilePicture?: string | null
		isImmutable?: boolean
		isVerified?: boolean
	}

	let {
		currentProfilePicture = $bindable(null),
		isImmutable = false,
		isVerified = false,
	}: Props = $props()

	const client = getContextClient()
	let fileInput: HTMLInputElement = $state()
	let isUploading = $state(false)
	let errorMessage = $state('')
	let isCropping = $state(false)
	let croppingCanvas: HTMLCanvasElement = $state()
	let ctx: CanvasRenderingContext2D
	let image: HTMLImageElement
	let dragStart: { x: number; y: number } | null = null
	let cropOffset = { x: 0, y: 0 }

	const allowedFormats = ['image/jpeg', 'image/png']
	const maxFileSize = 5 * 1024 * 1024 // 5 MB
	const displaySize = 160 // Size for display
	const canvasSize = 640 // Increased size for better resolution

	let imageUrl = $state('/visuals/icons/default-user.svg')
	$effect(() => {
		imageUrl = currentProfilePicture ?? '/visuals/icons/default-user.svg'
	})

// Initialize error handler with the image source and USER context
const handleImageLoadError = createImageErrorHandler(null, 'USER')

	function openFilePicker() {
		fileInput.click()
	}

	async function handleFileChange(event: Event) {
		const file = (event.target as HTMLInputElement).files[0]
		if (await validateFile(file)) {
			setupCropping(file)
		}
	}

	async function validateFile(file: File): Promise<boolean> {
		if (!file) return false

		if (!allowedFormats.includes(file.type)) {
			errorMessage = 'File format not supported. Please upload a JPEG or PNG image.'
			return false
		}

		if (file.size > maxFileSize) {
			errorMessage = 'File is too large. Please upload an image smaller than 5MB.'
			return false
		}

		errorMessage = ''
		return true
	}
	function setupCropping(file: File) {
		isCropping = true
		image = new Image()
		image.onload = () => {
			croppingCanvas.width = canvasSize
			croppingCanvas.height = canvasSize
			ctx = croppingCanvas.getContext('2d')

			// Calculate initial crop offset to center the image
			const size = Math.min(image.width, image.height)
			cropOffset = {
				x: (image.width - size) / 2,
				y: (image.height - size) / 2,
			}

			drawImage()
		}
		image.src = URL.createObjectURL(file)
	}

	function drawImage() {
		const size = Math.min(image.width, image.height)
		ctx.clearRect(0, 0, canvasSize, canvasSize)

		// Draw the full image with 50% opacity
		ctx.globalAlpha = 0.5
		ctx.drawImage(image, 0, 0, image.width, image.height, 0, 0, canvasSize, canvasSize)

		// Draw the circular crop area at full opacity
		ctx.globalAlpha = 1
		ctx.beginPath()
		ctx.arc(canvasSize / 2, canvasSize / 2, canvasSize / 2, 0, Math.PI * 2)
		ctx.closePath()
		ctx.clip()
		ctx.drawImage(image, cropOffset.x, cropOffset.y, size, size, 0, 0, canvasSize, canvasSize)
	}

	function handleDragStart(event: MouseEvent) {
		dragStart = { x: event.clientX, y: event.clientY }
	}

	function handleDragMove(event: MouseEvent) {
		if (!dragStart) return
		const dx = (event.clientX - dragStart.x) * (image.width / displaySize)
		const dy = (event.clientY - dragStart.y) * (image.height / displaySize)
		cropOffset.x = Math.max(
			0,
			Math.min(cropOffset.x - dx, image.width - Math.min(image.width, image.height)),
		)
		cropOffset.y = Math.max(
			0,
			Math.min(cropOffset.y - dy, image.height - Math.min(image.width, image.height)),
		)
		dragStart = { x: event.clientX, y: event.clientY }
		drawImage()
	}

	function handleDragEnd() {
		dragStart = null
	}

	async function cropAndUpload() {
		if (isImmutable) return
		isCropping = false

		// Create a temporary canvas for the final cropped image
		const tempCanvas = document.createElement('canvas')
		tempCanvas.width = canvasSize
		tempCanvas.height = canvasSize
		const tempCtx = tempCanvas.getContext('2d')

		// Draw only the cropped circular area
		tempCtx.beginPath()
		tempCtx.arc(canvasSize / 2, canvasSize / 2, canvasSize / 2, 0, Math.PI * 2)
		tempCtx.closePath()
		tempCtx.clip()
		tempCtx.drawImage(croppingCanvas, 0, 0, canvasSize, canvasSize)

		const blob = await new Promise<Blob>((resolve) =>
			tempCanvas.toBlob(resolve, 'image/jpeg', 0.95),
		)
		await uploadProfilePicture(blob)
	}
	async function uploadProfilePicture(blob: Blob) {
		if (isImmutable) return

		isUploading = true
		errorMessage = ''

		try {
			// 1. Get a fresh signed URL for each upload
			const { data: urlData, error: urlError } = await client
				.query<
					FetchSignedUploadUrlQuery,
					FetchSignedUploadUrlQueryVariables
				>(FetchSignedUploadUrlDocument, { mediaType: 'IMAGE' }, { fetchOptions: { cache: 'no-store' }, requestPolicy: 'network-only' })
				.toPromise()

			if (urlError || !urlData?.fetchSignedUploadUrl?.url) {
				throw new Error('Failed to get upload URL')
			}

			const { url, id } = urlData.fetchSignedUploadUrl

			// 2. Upload file to Cloudflare with error handling
			const uploadResult = await fetch(url, {
				method: 'POST',
				body: (() => {
					const formData = new FormData()
					formData.append('file', blob, 'profile.jpg')
					return formData
				})(),
			})

			if (!uploadResult.ok) {
				throw new Error(`Upload failed: ${uploadResult.status} ${uploadResult.statusText}`)
			}

			// 3. Update profile picture in database
			const { data: updateData, error: updateError } = await client
				.mutation<UpdateProfileMutation, UpdateProfileMutationVariables>(UpdateProfileDocument, {
					input: { cloudflareId: id },
				})
				.toPromise()

			if (updateError || !updateData?.updateProfile?.currentProfilePictureUrl) {
				throw new Error('Failed to update profile picture in database')
			}

			currentProfilePicture = updateData.updateProfile.currentProfilePictureUrl
		} catch (error) {
			console.error('Error uploading profile picture:', error)
			errorMessage = 'Failed to update profile picture. Please try again.'
			throw error // Re-throw to allow caller to handle retry logic if needed
		} finally {
			isUploading = false
		}
	}
</script>

<div class="w-full flex justify-center">
	<div class="relative">
		{#if isCropping}
			<div class="flex flex-col justify-center">
				<div
					class={'h-40 w-40 rounded-full bg-brand-gray-1 ring-1 ring-brand-gray-1 overflow-hidden'}
				>
					<canvas
						bind:this={croppingCanvas}
						class="h-40 w-40 cursor-move"
						style="width: {displaySize}px; height: {displaySize}px;"
						onmousedown={handleDragStart}
						onmousemove={handleDragMove}
						onmouseup={handleDragEnd}
						onmouseleave={handleDragEnd}
					></canvas>
				</div>
				<Button onclick={cropAndUpload} theme="brand" class="mt-2" size="xs">Crop and Save</Button>
			</div>
		{:else}
			<img
				src={imageUrl}
				onerror={handleImageLoadError}
				alt=""
				class={'h-40 w-40 rounded-full bg-brand-gray-1 ring-1 ring-brand-gray-1'}
			/>

			{#if isUploading}
				<div
					class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full"
				>
					<Spinner size="sm" />
				</div>
			{/if}
			{#if !isImmutable}
				<button
					onclick={stopPropagation(openFilePicker)}
					class="absolute bottom-0 right-0 bg-white dark:bg-brand-primary/80 hover:dark:bg-brand-primary rounded-full p-2 shadow-md touch-manipulation"
					disabled={isUploading}
				>
					<CameraIcon class="w-6 h-6 text-white dark:text-white" />
				</button>
			{:else if isVerified}
				<VerifiedIcon class="absolute h-10 w-10 bottom-0 right-0" />
			{/if}
		{/if}
	</div>
</div>

<input
	bind:this={fileInput}
	type="file"
	accept={allowedFormats.join(',')}
	onchange={handleFileChange}
	class="hidden"
/>

{#if errorMessage}
	<p class="text-red-500 text-sm mt-2">{errorMessage}</p>
{/if}
